import { useState } from 'react';
import { Button } from '@monksflow/monks-ui';
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';
import { tiptapToPlainText } from '../lib/serialize-brief';
import { useTranslation } from 'react-i18next';

export interface CommentSuggestion {
  sectionId: string;
  suggestedContent: Record<string, unknown>;
}

type SuggestionStatus = 'pending' | 'applied' | 'ignored';

interface Props {
  suggestion: CommentSuggestion;
  sectionExists: boolean;
  /** When 'applied' | 'ignored', the card renders a collapsed badge instead of the actions. */
  suggestionStatus?: SuggestionStatus | null;
  onApply: (suggestion: CommentSuggestion) => Promise<void>;
  onIgnore: (suggestion: CommentSuggestion) => Promise<void>;
}

/**
 * Applyable agent suggestion rendered below an agent comment. Apply replaces
 * the WHOLE target section's content with `suggestedContent` (brief PATCH) then
 * resolves the comment; Ignore just resolves it. Both record `suggestion_status`
 * so a handled suggestion shows a collapsed Applied/Ignored badge wherever it is
 * still rendered (e.g. under "Show resolved"). The badge toggles open to re-show
 * the (read-only) suggested content. To revert an applied suggestion, use the
 * section editor's own undo/redo — Apply lands in the editor's history. All
 * actions owned by the injected handlers.
 */
export function SuggestionBlock({
  suggestion,
  sectionExists,
  suggestionStatus,
  onApply,
  onIgnore,
}: Props) {
  const { t } = useTranslation('chats');

  const [isBusy, setIsBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const text = tiptapToPlainText(suggestion.suggestedContent);

  const run = (handler: () => Promise<void>) => {
    setIsBusy(true);
    void handler().finally(() => setIsBusy(false));
  };

  if (suggestionStatus === 'applied' || suggestionStatus === 'ignored') {
    const applied = suggestionStatus === 'applied';
    return (
      <div className="mt-2 flex flex-col gap-1.5">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
          aria-expanded={expanded}
        >
          {applied ? <Check className="size-3.5" /> : <X className="size-3.5" />}
          {applied
            ? t('briefAgentReview.suggestionBlock.applied')
            : t('briefAgentReview.suggestionBlock.ignored')}
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
        {expanded && (
          <p className="whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-sm text-foreground">
            {text}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-3">
      <span className="text-xs font-medium text-muted-foreground">
        {t('briefAgentReview.suggestionBlock.suggestedReplacement')}
      </span>
      <p className="whitespace-pre-wrap text-sm text-foreground">{text}</p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!sectionExists || isBusy}
          onClick={() => run(() => onApply(suggestion))}
        >
          {isBusy
            ? t('briefAgentReview.suggestionBlock.working')
            : t('briefAgentReview.suggestionBlock.apply')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={isBusy}
          onClick={() => run(() => onIgnore(suggestion))}
        >
          {t('briefAgentReview.suggestionBlock.ignore')}
        </Button>
        {!sectionExists && (
          <span className="text-xs text-muted-foreground">
            {t('briefAgentReview.suggestionBlock.sectionNoLongerExists')}
          </span>
        )}
      </div>
    </div>
  );
}
